import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client/core'
import { CombinedGraphQLErrors, CombinedProtocolErrors } from '@apollo/client/errors'
import { BatchHttpLink } from '@apollo/client/link/batch-http'
import { SetContextLink } from '@apollo/client/link/context'
import { ErrorLink } from '@apollo/client/link/error'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { context, trace } from '@opentelemetry/api'
import 'isomorphic-fetch'
import { getPublicEnv } from '@tucan/utils'
import debug from 'debug'
import { createClient } from 'graphql-ws'
import { OperationTypeNode } from 'graphql/language/ast'
import { Kind } from 'graphql/language/kinds'
import WebSocket from 'isomorphic-ws'
import { getNumber } from '../utils/get.number'
import { isBrowser } from '../utils/is.browser'
import { getGraphQLUrl } from './get.graphql.url'
import { tokenRefreshLink } from './token.link'

const logger = debug('gql')

let client: ApolloClient

export const hasGraphQLError = (e: any, message): boolean => {
  if (!CombinedGraphQLErrors.is(e)) {
    return false
  }

  for (const error of e.errors) {
    if (error.message === message) {
      return true
    }
  }

  return false
}

const getClient = (): ApolloClient => {
  if (!client) {
    client = new ApolloClient({
      cache: new InMemoryCache(),
      link: ApolloLink.from([
        new ErrorLink(({ error, operation }) => {
          if (CombinedGraphQLErrors.is(error)) {
            error.errors.forEach(({ message, locations, path }) =>
              logger(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
            )
          } else if (CombinedProtocolErrors.is(error)) {
            error.errors.forEach(({ message, extensions }) =>
              logger(
                `[Protocol error]: Message: ${message}, Extensions: ${JSON.stringify(extensions)}`
              )
            )
          } else {
            logger(`[Network error]: ${error}`)
          }
        }),
        tokenRefreshLink,
        new SetContextLink(async (ctx, request) => {
          const headers: any = {}

          if (isBrowser()) {
            if (ctx.recaptcha) {
              headers.recaptcha = ctx.recaptcha
            }
            if (!ctx.ignoreCompanyHeader) {
              headers['x-company-id'] = localStorage.getItem('company')
            }

            headers['x-language'] = localStorage.getItem('i18nextLng')
          } else {
            const span = trace.getSpan(context.active())
            if (span) {
              const { spanId, traceId } = span.spanContext()

              headers['X-B3-TraceId'] = traceId
              headers['X-B3-ParentSpanId'] = spanId
            }
          }

          if (ctx.Authorization) {
            headers['Authorization'] = ctx.Authorization
          }

          if (ctx['x-company-id']) {
            headers['x-company-id'] = ctx['x-company-id']
          }

          if (ctx['x-language']) {
            headers['x-language'] = ctx['x-language']
          }

          return {
            headers,
          }
        }),
        ApolloLink.split(
          ({ query }) => {
            const definition = getMainDefinition(query)
            return (
              definition.kind === Kind.OPERATION_DEFINITION &&
              definition.operation === OperationTypeNode.SUBSCRIPTION
            )
          },
          new GraphQLWsLink(
            createClient({
              connectionParams: () => {
                if (!isBrowser()) {
                  return {}
                }

                return {
                  isWebSocket: true,
                }
              },
              url: getGraphQLUrl({ websocket: true }),
              webSocketImpl: WebSocket,
            })
          ),
          ApolloLink.split(
            (op) => {
              if (getNumber(getPublicEnv('GRAPHQL_BATCH_INTERVAL')) === 0) {
                return false
              }

              const batch = op.getContext()['batch']

              return batch === undefined || batch === true
            },
            new BatchHttpLink({
              batchInterval: getNumber(getPublicEnv('GRAPHQL_BATCH_INTERVAL')),
              uri: getGraphQLUrl(),
            }),
            new HttpLink({
              uri: getGraphQLUrl(),
            })
          )
        ),
      ]),
    })
  }

  return client
}

export default getClient
